import { useFadeIn } from '@/hooks/useFadeIn';

interface DoctorCardProps {
  role: string;
  nameJa: string;
  nameEn: string;
  education: string;
  career: string;
  speciality: string;
  message: string;
  imgSrc: string;
  delay: number;
}

function DoctorCard({
  role,
  nameJa,
  nameEn,
  education,
  career,
  speciality,
  message,
  imgSrc,
  delay,
}: DoctorCardProps) {
  const fade = useFadeIn({ direction: 'up', delay });
  return (
    <div
      ref={fade.ref}
      style={fade.style}
      className="bg-white rounded-lg border-t border-gray-200 overflow-hidden"
    >
      <div className="flex flex-col md:flex-row">
        <div className="md:w-2/5 flex-shrink-0">
          <div className="aspect-[3/4] md:aspect-auto md:h-full overflow-hidden">
            <img
              alt={nameJa}
              className="w-full h-full object-cover object-top"
              src={imgSrc}
            />
          </div>
        </div>
        <div className="p-5 md:p-6 md:w-3/5 flex flex-col">
          <div className="mb-4">
            <span
              className="inline-block font-medium text-xs tracking-wider mb-1"
              style={{ color: '#3E5F8F' }}
            >
              {role}
            </span>
            <h3 className="font-serif text-gray-900 text-xl md:text-2xl leading-relaxed">
              {nameJa}
            </h3>
            <span className="font-light text-gray-500 text-xs tracking-wider">{nameEn}</span>
          </div>
          <div className="space-y-3 mb-4 flex-1">
            <div className="flex items-start gap-2">
              <i
                className="ri-graduation-cap-line text-sm mt-0.5 flex-shrink-0 w-4 h-4 flex items-center justify-center"
                style={{ color: '#3E5F8F' }}
              ></i>
              <p className="font-light text-gray-700 text-sm leading-relaxed">{education}</p>
            </div>
            <div className="flex items-start gap-2">
              <i
                className="ri-hospital-line text-sm mt-0.5 flex-shrink-0 w-4 h-4 flex items-center justify-center"
                style={{ color: '#3E5F8F' }}
              ></i>
              <p className="font-light text-gray-700 text-sm leading-relaxed">{career}</p>
            </div>
            <div className="flex items-start gap-2">
              <i
                className="ri-stethoscope-line text-sm mt-0.5 flex-shrink-0 w-4 h-4 flex items-center justify-center"
                style={{ color: '#3E5F8F' }}
              ></i>
              <p className="font-light text-gray-700 text-sm leading-relaxed">{speciality}</p>
            </div>
          </div>
          <div
            className="rounded-lg p-4 mt-auto bg-gray-50"
            style={{ borderLeft: '4px solid #3E5F8F' }}
          >
            <p className="font-light text-gray-700 text-sm leading-relaxed italic">
              「{message}」
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

const doctors = [
  {
    role: '美容歯科医師',
    nameJa: '関姫乃',
    nameEn: 'Himeno Seki',
    education: '日本歯科大学新潟 歯学部 卒業',
    career:
      '大学病院・総合病院 勤務後、さいわいデンタルクリニックに勤務。ICON白斑治療やアライナー矯正など、数多くの審美治療に従事。',
    speciality: 'ホワイトスポット治療、マウスピース矯正、ホワイトニング',
    message:
      '歯の表面にある白い模様が少しでも気になる方は、ぜひ一度ご相談ください。他院で「削るしかない」と言われた状態でも、ICONなら削らずに自然な美しさを取り戻せる場合があります。状態に合わせて最適な治療プランを丁寧にご提案させていただきます。',
    imgSrc:
      'https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/85f57ced37f9fa32a4e58c70eb0a4c82.png',
  },
  {
    role: '保存治療・補綴治療 専門医',
    nameJa: '秋山隼人',
    nameEn: 'Hayato Akiyama',
    education: '北海道医療大学 歯学部 卒業',
    career:
      '札幌市内の歯科医院に勤務後、さいわいデンタルクリニックに勤務。予防歯科と保存治療を軸に、健康な歯を長く保つ治療を心がけている。',
    speciality: '歯科保存治療、予防歯科、小児歯科、根管治療',
    message:
      '初期虫歯の予防や審美面で気になるけど削りたくない…と言う方は大勢いらっしゃると思います。そういう方に白い部分を自然な見た目に改善できるICON治療をお勧めしております。',
    imgSrc:
      'https://static.readdy.ai/image/cff737b9fe944b0ea10e9e46c08321bb/05a3e25187f6df98c7bd9badbd06d95f.jpeg',
  },
];

export default function IconDoctor() {
  const heading = useFadeIn({ direction: 'up' });

  return (
    <section id="doctor" className="bg-white py-14 md:py-24">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div ref={heading.ref} style={heading.style}>
          <span
            className="inline-block font-medium text-xs md:text-sm tracking-[0.2em]"
            style={{ color: '#3E5F8F' }}
          >
            Doctors
          </span>
          <div className="w-8 h-px mt-3 mb-4" style={{ backgroundColor: '#3E5F8F' }}></div>
          <h2 className="font-serif text-gray-900 text-2xl md:text-3xl leading-relaxed mb-3">
            担当医師のご紹介
          </h2>
          <p className="font-light text-gray-500 text-sm leading-relaxed max-w-2xl">
            白斑治療に精通した専門医が、丁寧にご対応いたします。
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mt-10 md:mt-12">
          {doctors.map((doc, i) => (
            <DoctorCard key={doc.nameJa} {...doc} delay={i * 150} />
          ))}
        </div>
      </div>
    </section>
  );
}